import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { pilotDocumentFetchUrl } from '../utils/pilotDocumentUrl';

const emptyForm = () => ({
  password: '',
  name: '',
  email: '',
  phone: '',
  aircraft_type: '',
  aircraft_registration: '',
  aircraft_types: '',
  total_flight_hours: '',
  recent_flight_hours: '',
  hours_on_type: '',
  licence_qualification: '',
  ratings: '',
  license_valid_until: '',
  medical_valid_until: '',
  pax_certificate_date: ''
});

function toForm(profile) {
  return {
    password: '',
    name: profile.name || '',
    email: profile.email || '',
    phone: profile.phone || '',
    aircraft_type: profile.aircraft_type || '',
    aircraft_registration: profile.aircraft_registration || '',
    aircraft_types: profile.aircraft_types || '',
    total_flight_hours: profile.total_flight_hours ?? '',
    recent_flight_hours: profile.recent_flight_hours ?? '',
    hours_on_type: profile.hours_on_type ?? '',
    licence_qualification: profile.licence_qualification || '',
    ratings: profile.ratings || '',
    license_valid_until: (profile.license_valid_until || '').toString().slice(0, 10),
    medical_valid_until: (profile.medical_valid_until || '').toString().slice(0, 10),
    pax_certificate_date: (profile.pax_certificate_date || '').toString().slice(0, 10)
  };
}

export default function PilotMyProfilePanel({ onSuccess, onError }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [profile, setProfile] = useState(null);
  const [canSubmit, setCanSubmit] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [docDates, setDocDates] = useState({ license_valid_until: '', medical_valid_until: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/pilot-profile/me');
      const p = res.data.profile;
      setProfile(p);
      setCanSubmit(Boolean(res.data.canSubmitChanges));
      setPendingRequests(res.data.pendingRequests || []);
      setForm(toForm(p));
      setDocDates({
        license_valid_until: (p.license_valid_until || '').toString().slice(0, 10),
        medical_valid_until: (p.medical_valid_until || '').toString().slice(0, 10)
      });
    } catch (err) {
      onError?.(err.response?.data?.error || t('admin.pilotProfile.loadError'));
    } finally {
      setLoading(false);
    }
  }, [onError, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      await axios.post('/api/pilot-profile/me', payload);
      onSuccess?.(t('admin.pilotProfile.submitSuccess'));
      await load();
    } catch (err) {
      onError?.(err.response?.data?.error || t('admin.pilotProfile.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  const uploadDocument = async (documentType, file) => {
    if (!file) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append(documentType, file);
      if (documentType === 'license' && docDates.license_valid_until) {
        fd.append('license_valid_until', docDates.license_valid_until);
      }
      if (documentType === 'medical' && docDates.medical_valid_until) {
        fd.append('medical_valid_until', docDates.medical_valid_until);
      }
      await axios.post(`/api/pilot-profile/me/documents/${documentType}`, fd);
      onSuccess?.(t('admin.pilotProfile.docSubmitSuccess'));
      await load();
    } catch (err) {
      onError?.(err.response?.data?.error || t('admin.pilotProfile.docSubmitError'));
    } finally {
      setSubmitting(false);
    }
  };

  const serveDoc = async (docPath) => {
    if (!docPath) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(pilotDocumentFetchUrl(docPath), {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) throw new Error('load failed');
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer');
    } catch {
      onError?.(t('admin.messages.docLoadErr'));
    }
  };

  if (loading) {
    return <p className="loading">{t('admin.loading')}</p>;
  }

  if (!profile) {
    return <p>{t('admin.pilotProfile.loadError')}</p>;
  }

  return (
    <div className="pilot-my-profile">
      <p className="section-desc">{t('admin.pilotProfile.intro')}</p>

      {!canSubmit && (
        <div className="error" role="alert">
          {t('admin.pilotProfile.notApproved')}
        </div>
      )}

      {pendingRequests.length > 0 && (
        <div className="card pilot-profile-pending-card">
          <h3>{t('admin.pilotProfile.pendingTitle')}</h3>
          <ul className="pilot-profile-pending-list">
            {pendingRequests.map((r) => (
              <li key={r.id}>
                <span className="pilot-profile-pending-kind">
                  {r.changeKind === 'document'
                    ? t('admin.pilotProfile.pendingDoc')
                    : t('admin.pilotProfile.pendingProfile')}
                </span>
                <span className="pilot-profile-pending-summary">{r.summary}</span>
                <span className="pilot-profile-pending-date">
                  {r.createdAt ? new Date(r.createdAt).toLocaleString() : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="card">
        <h3>{t('admin.pilotProfile.profileTitle')}</h3>
        <form onSubmit={handleProfileSubmit}>
          <div className="form-group">
            <label>{t('admin.instructors.password')}</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder={t('admin.pilotProfile.passwordPh')}
              disabled={!canSubmit || submitting}
            />
          </div>
          <div className="form-group">
            <label>{t('admin.instructors.name')}</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              disabled={!canSubmit || submitting}
            />
          </div>
          <div className="form-group">
            <label>{t('admin.instructors.email')}</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              disabled={!canSubmit || submitting}
            />
          </div>
          <div className="form-group">
            <label>{t('admin.instructors.phone')}</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              disabled={!canSubmit || submitting}
            />
          </div>
          <div className="form-group">
            <label>{t('admin.instructors.aircraftType')}</label>
            <input
              type="text"
              value={form.aircraft_type}
              onChange={(e) => setForm({ ...form, aircraft_type: e.target.value })}
              disabled={!canSubmit || submitting}
            />
          </div>
          <div className="form-group">
            <label>{t('admin.instructors.aircraftReg')}</label>
            <input
              type="text"
              value={form.aircraft_registration}
              onChange={(e) => setForm({ ...form, aircraft_registration: e.target.value })}
              disabled={!canSubmit || submitting}
            />
          </div>
          <div className="form-group">
            <label>{t('admin.instructors.aircraftTypes')}</label>
            <input
              type="text"
              value={form.aircraft_types}
              onChange={(e) => setForm({ ...form, aircraft_types: e.target.value })}
              disabled={!canSubmit || submitting}
            />
          </div>
          <div className="form-group">
            <label>{t('admin.instructors.totalHours')}</label>
            <input
              type="number"
              min="0"
              step="0.1"
              value={form.total_flight_hours}
              onChange={(e) => setForm({ ...form, total_flight_hours: e.target.value })}
              disabled={!canSubmit || submitting}
            />
          </div>
          <div className="form-group">
            <label>{t('admin.instructors.recentHours')}</label>
            <input
              type="number"
              min="0"
              step="0.1"
              value={form.recent_flight_hours}
              onChange={(e) => setForm({ ...form, recent_flight_hours: e.target.value })}
              disabled={!canSubmit || submitting}
            />
          </div>
          <div className="form-group">
            <label>{t('admin.instructors.hoursOnType')}</label>
            <input
              type="number"
              min="0"
              step="0.1"
              value={form.hours_on_type}
              onChange={(e) => setForm({ ...form, hours_on_type: e.target.value })}
              disabled={!canSubmit || submitting}
            />
          </div>
          <div className="form-group">
            <label>{t('admin.instructors.licenceQualification')}</label>
            <input
              type="text"
              value={form.licence_qualification}
              onChange={(e) => setForm({ ...form, licence_qualification: e.target.value })}
              disabled={!canSubmit || submitting}
            />
          </div>
          <div className="form-group">
            <label>{t('admin.instructors.ratingsPublic')}</label>
            <input
              type="text"
              value={form.ratings}
              onChange={(e) => setForm({ ...form, ratings: e.target.value })}
              disabled={!canSubmit || submitting}
            />
          </div>
          <div className="form-group">
            <label>{t('admin.instructors.sepValid')}</label>
            <input
              type="date"
              value={form.license_valid_until}
              onChange={(e) => setForm({ ...form, license_valid_until: e.target.value })}
              disabled={!canSubmit || submitting}
            />
          </div>
          <div className="form-group">
            <label>{t('admin.instructors.medicalValid')}</label>
            <input
              type="date"
              value={form.medical_valid_until}
              onChange={(e) => setForm({ ...form, medical_valid_until: e.target.value })}
              disabled={!canSubmit || submitting}
            />
          </div>
          <div className="form-group">
            <label>{t('admin.instructors.paxCert')}</label>
            <input
              type="date"
              value={form.pax_certificate_date}
              onChange={(e) => setForm({ ...form, pax_certificate_date: e.target.value })}
              disabled={!canSubmit || submitting}
            />
          </div>
          <p className="form-hint">{t('admin.pilotProfile.submitHint')}</p>
          <button type="submit" className="btn btn-success" disabled={!canSubmit || submitting}>
            {submitting ? t('admin.pilotProfile.submitting') : t('admin.pilotProfile.submitProfile')}
          </button>
        </form>
      </div>

      <div className="card pilot-profile-docs-card">
        <h3>{t('admin.pilotProfile.documentsTitle')}</h3>
        <p className="form-hint">{t('admin.pilotProfile.documentsHint')}</p>

        <DocRow
          label={t('admin.instructors.docLicense')}
          docPath={profile.license_document_path}
          dateValue={docDates.license_valid_until}
          dateTitle={t('admin.instructors.sepValid')}
          onDateChange={(v) => setDocDates({ ...docDates, license_valid_until: v })}
          onView={() => serveDoc(profile.license_document_path)}
          onFile={(f) => uploadDocument('license', f)}
          canSubmit={canSubmit}
          submitting={submitting}
          viewLabel={t('admin.instructors.viewDownload')}
        />
        <DocRow
          label={t('admin.instructors.docMedical')}
          docPath={profile.medical_document_path}
          dateValue={docDates.medical_valid_until}
          dateTitle={t('admin.instructors.medicalValid')}
          onDateChange={(v) => setDocDates({ ...docDates, medical_valid_until: v })}
          onView={() => serveDoc(profile.medical_document_path)}
          onFile={(f) => uploadDocument('medical', f)}
          canSubmit={canSubmit}
          submitting={submitting}
          viewLabel={t('admin.instructors.viewDownload')}
        />
        <DocRow
          label={t('admin.instructors.docLogbook')}
          docPath={profile.logbook_last_page_path}
          onView={() => serveDoc(profile.logbook_last_page_path)}
          onFile={(f) => uploadDocument('logbook', f)}
          canSubmit={canSubmit}
          submitting={submitting}
          viewLabel={t('admin.instructors.viewDownload')}
        />
      </div>
    </div>
  );
}

function DocRow({
  label,
  docPath,
  dateValue,
  dateTitle,
  onDateChange,
  onView,
  onFile,
  canSubmit,
  submitting,
  viewLabel
}) {
  return (
    <div className="pilot-admin-doc-row">
      <span className="pilot-admin-doc-label">{label}</span>
      <div className="pilot-admin-doc-actions">
        {docPath && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={onView}>
            {viewLabel}
          </button>
        )}
        {onDateChange && (
          <input
            type="date"
            value={dateValue || ''}
            onChange={(e) => onDateChange(e.target.value)}
            disabled={!canSubmit || submitting}
            title={dateTitle}
          />
        )}
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,application/pdf"
          className="pilot-admin-doc-file"
          disabled={!canSubmit || submitting}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) onFile(f);
          }}
        />
      </div>
    </div>
  );
}
